import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BrandExpandCardsDivider, BRAND_CARD_GRID_INITIAL_COUNT } from './BrandExpandCardsDivider';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/root-reducer';
import { getUsersDashboard } from '../../redux/user/actions';
import { GigsData } from '../../redux/user/types';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import moment from 'moment';
import { formatDate, formatNumberWithK } from '../../utils/AppUtils';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { navigate } from '../../redux/navigator/action';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';

const radius = '12px';

type AuditionRow = GigsData & { slug?: string };

function isAuditionNotExpired(casting_expiration: string | undefined): boolean {
    if (!casting_expiration) return false;
    return !moment(casting_expiration).isBefore(moment());
}

function BrandAuditionCard({ audition }: { audition: AuditionRow }) {
    const dispatch = useDispatch();

    const goToAudition = useCallback(() => {
        const id = audition._id || audition.slug;
        if (!id) return;
        const route = replaceRouteParam(ROUTES.BRAND_AUDITION_DETAIL, 'auditionId', id);
        dispatch(navigate({ routes: route, skipAuthentication: true }));
    }, [audition._id, audition.slug, dispatch]);

    const handleIconClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            goToAudition();
        },
        [goToAudition],
    );

    const tags = audition.tags || [];
    const maxTagPills = 1;
    const visibleTags = tags.slice(0, maxTagPills);
    const overflowTagCount = Math.max(0, tags.length - maxTagPills);

    return (
        <Box
            onClick={goToAudition}
            sx={{
                p: 3,
                borderRadius: radius,
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                cursor: 'pointer',
                transition: 'background 0.2s',
                '&:hover': { bgcolor: BRAND_SURFACE_CONTAINER },
            }}>
            {audition.imageCover ? (
                <Box
                    sx={{
                        width: { xs: '100%', md: 128 },
                        height: 128,
                        borderRadius: radius,
                        overflow: 'hidden',
                        flexShrink: 0,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                    }}>
                    <Box
                        component="img"
                        src={audition.imageCover}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        width: { xs: '100%', md: 128 },
                        height: 128,
                        borderRadius: radius,
                        flexShrink: 0,
                        bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                    }}
                />
            )}

            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minWidth: 0,
                }}>
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 1,
                            mb: 1,
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: 17, sm: 18 },
                                fontWeight: 700,
                                color: '#fafafa',
                                lineHeight: 1.35,
                                pr: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                            }}>
                            {audition.title}
                        </Typography>
                        <IconButton
                            size="small"
                            aria-label="Open audition page"
                            onClick={handleIconClick}
                            sx={{
                                color: BRAND_LUXE_SECONDARY,
                                flexShrink: 0,
                                position: 'relative',
                                width: 28,
                                height: 28,
                                transition: 'transform 0.2s ease',
                                '&:hover': {
                                    color: BRAND_LUXE_SECONDARY,
                                    bgcolor: 'transparent',
                                    transform: 'scale(1.05)',
                                },
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    borderRadius: '50%',
                                    backgroundColor: BRAND_LUXE_SECONDARY + '00',
                                    transform: 'scale(1)',
                                    transition: 'transform 0.2s ease, background-color 0.2s ease',
                                    zIndex: -1,
                                },
                                '&:hover::before': {
                                    transform: 'scale(1.45)',
                                    backgroundColor: BRAND_LUXE_SECONDARY + '1F',
                                },
                            }}>
                            <OpenInNewOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
                        {visibleTags.map((tag, i) => (
                            <Typography
                                key={`${tag}-${i}`}
                                component="span"
                                sx={{
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'capitalize',
                                    letterSpacing: '0.04em',
                                    color: BRAND_LUXE_SECONDARY,
                                    bgcolor: BRAND_LUXE_SECONDARY + '18',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                }}>
                                {tag}
                            </Typography>
                        ))}
                        {overflowTagCount > 0 && (
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'capitalize',
                                    letterSpacing: '0.04em',
                                    color: BRAND_LUXE_SECONDARY,
                                    bgcolor: BRAND_LUXE_SECONDARY + '18',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                }}>
                                +{overflowTagCount} more
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        /** Below `md`: Expiration + Created only; Created tucks under ⋮ */
                        gridTemplateColumns: { xs: '1fr max-content', md: 'auto 1fr max-content' },
                        gap: 2,
                        pt: 2,
                        alignItems: 'start',
                    }}>
                    <Box sx={{ minWidth: 0, justifySelf: { md: 'start' } }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: '#adaaaa',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                mb: 0.5,
                            }}>
                            Expiration
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: '#fafafa' }}>
                            {formatDate(audition.casting_expiration, 'MMM D, YY')}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            textAlign: { xs: 'right', md: 'center' },
                            justifySelf: { xs: 'end', md: 'center' },
                            minWidth: 0,
                            /** xs: tuck under ⋮; md: balanced middle band between Expiration and Applications */
                            pr: { xs: 0.25, md: 0 },
                        }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: '#adaaaa',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                mb: 0.5,
                            }}>
                            Created
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: '#fafafa' }}>
                            {formatDate(audition.createdAt || '', 'MMM D, YY')}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            width: 'max-content',
                            justifySelf: 'end',
                            /** Column stays under ⋮; label stays right, count matches other columns (left). */
                            pr: { md: 0.5 },
                        }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: '#adaaaa',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                mb: 0.5,
                                textAlign: 'right',
                            }}>
                            Applications
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 14,
                                color: BRAND_LUXE_SECONDARY,
                                textAlign: 'center',
                            }}>
                            {formatNumberWithK(audition.totalApplications ?? 0)}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export function BrandDashboardActiveAuditions() {
    const dispatch = useDispatch();
    const gigs = useSelector((s: RootState) => s.user.dashboardData.gigs.data) as AuditionRow[];
    const gettingDashboard = useSelector((s: RootState) => s.user.gettingDashboardData);
    const [auditionsExpanded, setAuditionsExpanded] = useState(false);

    useEffect(() => {
        dispatch(getUsersDashboard({ page: 1, limit: 10, category: 'gigs' }));
    }, [dispatch]);

    const allRows = useMemo(() => gigs || [], [gigs]);
    const rows = useMemo(() => allRows.filter(a => isAuditionNotExpired(a.casting_expiration)), [allRows]);

    useEffect(() => {
        if (rows.length <= BRAND_CARD_GRID_INITIAL_COUNT) {
            setAuditionsExpanded(false);
        }
    }, [rows.length]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 24,
                        fontWeight: 900,
                        letterSpacing: '-0.02em',
                    }}>
                    Active auditions
                </Typography>
                <Typography
                    component={Link}
                    to={ROUTES.AUDITION}
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 14,
                        color: '#feb700',
                        textDecoration: 'none',
                        borderBottom: '1px solid rgba(254,183,0,0.2)',
                        '&:hover': { borderBottomColor: '#feb700' },
                    }}>
                    View all
                </Typography>
            </Box>

            {gettingDashboard && allRows.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
                </Box>
            ) : rows.length > 0 ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3 }}>
                    {rows.slice(0, BRAND_CARD_GRID_INITIAL_COUNT).map(a => (
                        <BrandAuditionCard key={a._id} audition={a} />
                    ))}
                    {rows.length > BRAND_CARD_GRID_INITIAL_COUNT && (
                        <>
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <BrandExpandCardsDivider
                                    expanded={auditionsExpanded}
                                    onToggle={() => setAuditionsExpanded(v => !v)}
                                    sectionLabel="auditions"
                                />
                            </Box>
                            {auditionsExpanded &&
                                rows
                                    .slice(BRAND_CARD_GRID_INITIAL_COUNT)
                                    .map(a => <BrandAuditionCard key={a._id} audition={a} />)}
                        </>
                    )}
                </Box>
            ) : (
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: '#adaaaa' }}>
                    {allRows.length > 0
                        ? 'No active auditions — all listed auditions have passed their expiration date.'
                        : 'No auditions yet. Create an audition from your dashboard to see it here.'}
                </Typography>
            )}
        </Box>
    );
}
